import { ForecastModelDataEntryStatus, ForecastModelDataEntryType, ForecastModelRecordDataImportPeriodType, RequestStatus, VATPeriodType, defaults, entities } from 'typed/api';
import {
    childrenReducer, combineReducers, entityAddAllReducer, entityAddReducer, entityDeleteAllReducer,
    entityDeleteReducer, entityEditAllReducer, entityEditReducer, entityLoadAllReducer, entityLoadReducer, entityVoidAllReducer,
    networkActionReducer, sortedEntitiesReducer, switchReducers, synchronizableEnumReducer
} from './utils';
import { deepAssign } from 'typed/utils';
import { Action } from 'typed/actions';


// TODO improve how `switchReducers` and `combineReducers` work; both of them have `defaultState` parameter, which is applied before any call
// so their children reducers will never face an empty state and therefore don't need `defaultState`
// so there should be special type of reducer without empty state option, and special versions of entity*Reducer without `defaultState` generating those special reducers
// but that's a lot of work...
// for now let's reduce amount of visual noise and accesses to `defaults` (which actually might be slow since every attempt to access creates a new copy of an entity)
// with these very short aliases
const
    ms = defaults.NetworkArrayOf(defaults.ForecastModelReport),
    m = defaults.ForecastModelReport,
    ls = [] as entities.ForecastModelAggregationLevelReport[],
    l = defaults.ForecastModelAggregationLevelReport,
    as = defaults.NetworkArrayOf(defaults.ForecastModelAggregationReport),
    es = [] as entities.ForecastModelDataEntry[],
    e = defaults.ForecastModelDataEntry,
    fr = defaults.ForecastModelRecordDataReport,
    frs = defaults.NetworkArrayOf(fr),
    frfs: entities.ForecastModelRecordDataFileReport[] = [],
    frf = defaults.ForecastModelRecordDataFileReport,
    frfas: entities.ForecastModelRecordDataActualSplitRange[] = [],
    f = defaults.CashflowForecastSettingsReport,
    cs = [] as entities.CashflowForecastIndicatorConnectionReport[],
    c = defaults.CashflowForecastIndicatorConnectionReport,
    ds = [] as entities.CashflowForecastIndicatorDistributionEntry[],
    r = defaults.CashflowForecastDataReport;

export default combineReducers({
    forecastModelDataEntryTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.ForecastModelDataEntryType), 'LOAD_ALL_FORECAST_MODEL_DATA_ENTRY_TYPES', ForecastModelDataEntryType as any, 'ForecastModelDataEntryType'),
    forecastModelDataEntryStatuses: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.ForecastModelDataEntryStatus), 'LOAD_ALL_FORECAST_MODEL_DATA_ENTRY_STATUSES', ForecastModelDataEntryStatus as any, 'ForecastModelDataEntryStatus'),
    forecastModels: sortedEntitiesReducer(ms, 'Name', switchReducers(ms,
        entityLoadAllReducer(ms, 'LOAD_ALL_FORECAST_MODELS'),
        entityLoadReducer(ms, 'LOAD_FORECAST_MODEL'),
        entityAddReducer(ms, 'ADD_FORECAST_MODEL'),
        entityEditReducer(ms, 'EDIT_FORECAST_MODEL'),
        childrenReducer(ms,
            combineReducers(m, {
                Levels: switchReducers(ls,
                    entityLoadReducer(ls, 'LOAD_FORECAST_MODEL_AGGREGATION_LEVEL'),
                    entityAddAllReducer(ls, 'ADD_ALL_FORECAST_MODEL_AGGREGATION_LEVELS'),
                    entityAddReducer(ls, 'ADD_FORECAST_MODEL_AGGREGATION_LEVEL'),
                    entityEditAllReducer(ls, 'EDIT_ALL_FORECAST_MODEL_AGGREGATION_LEVELS'),
                    entityEditReducer(ls, 'EDIT_FORECAST_MODEL_AGGREGATION_LEVEL'),
                    childrenReducer(ls,
                        combineReducers(l, {
                            LastUpdate: (state: Date | null = null, action: Action<'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA'>) =>
                                action.type === 'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA' && !action.payload.preview && action.payload.status === RequestStatus.SUCCEEDED ?
                                    new Date() :
                                    state,
                            Entries: sortedEntitiesReducer(es, 'Name', switchReducers(es,
                                entityLoadReducer(es, 'LOAD_FORECAST_MODEL_DATA_ENTRY'),
                                entityAddReducer(es, 'ADD_FORECAST_MODEL_DATA_ENTRY'),
                                entityEditReducer(es, 'EDIT_FORECAST_MODEL_DATA_ENTRY'),
                                childrenReducer(es,
                                    switchReducers(e,
                                        networkActionReducer(e, 'SEND_FORECAST_MODEL_DATA_ENTRY_REMINDER'),
                                        (state = e, action: Action<'ADD_FORECAST_MODEL_DATA_ENTRY' | 'EDIT_FORECAST_MODEL_DATA_ENTRY' | 'SEND_FORECAST_MODEL_DATA_ENTRY_REMINDER'>) =>
                                            action.type === 'ADD_FORECAST_MODEL_DATA_ENTRY' && action.payload.status === RequestStatus.SUCCEEDED && (action.payload as any).withReminder ||
                                                        action.type === 'EDIT_FORECAST_MODEL_DATA_ENTRY' && action.payload.status === RequestStatus.SUCCEEDED && (action.payload as any).withReminder ||
                                                        action.type === 'SEND_FORECAST_MODEL_DATA_ENTRY_REMINDER' && action.payload.status === RequestStatus.SUCCEEDED ?
                                                deepAssign(state,
                                                    {
                                                        StatusID: action.type === 'SEND_FORECAST_MODEL_DATA_ENTRY_REMINDER' ?
                                                            action.payload.statusID :
                                                            action.payload.entity.StatusID,
                                                        ManagerNote: action.type === 'SEND_FORECAST_MODEL_DATA_ENTRY_REMINDER' ?
                                                            action.payload.managerNote :
                                                            action.payload.entity.ManagerNote,
                                                        LastReminderSent: new Date()
                                                    }) :
                                                state,
                                        networkActionReducer(e, 'TOUCH_FORECAST_MODEL_DATA_ENTRY'),
                                        (state = e, action: Action<'TOUCH_FORECAST_MODEL_DATA_ENTRY'>) =>
                                            action.type === 'TOUCH_FORECAST_MODEL_DATA_ENTRY' && action.payload.status === RequestStatus.SUCCEEDED ?
                                                deepAssign(state,
                                                    {
                                                        StatusID: action.payload.statusID,
                                                        ResponsibleUserNote: action.payload.responsibleUserNote,
                                                        LastUpdate: new Date()
                                                    }) :
                                                state,
                                        combineReducers(e, {
                                            Custom: combineReducers(e.Custom, {
                                                status: (state: RequestStatus = e.Custom.status, action: Action<'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA' | 'ADD_FORECAST_MODEL_DATA_ENTRY' | 'EDIT_FORECAST_MODEL_DATA_ENTRY'>) =>
                                                    action.type === 'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA' && action.payload.status === RequestStatus.SUCCEEDED ||
                                                                action.type === 'ADD_FORECAST_MODEL_DATA_ENTRY' && action.payload.entity.TypeID === ForecastModelDataEntryType.GOOGLE_SHEET && action.payload.status === RequestStatus.SUCCEEDED ||
                                                                action.type === 'EDIT_FORECAST_MODEL_DATA_ENTRY' && action.payload.entity.TypeID === ForecastModelDataEntryType.GOOGLE_SHEET && action.payload.status === RequestStatus.SUCCEEDED ?
                                                        RequestStatus.UNSYNCED as RequestStatus :
                                                        state,
                                                template: (state = e.Custom.template, action: Action<'GENERATE_FORECAST_MODEL_DATA_ENTRY_TEMPLATE'>) =>
                                                    action.type === 'GENERATE_FORECAST_MODEL_DATA_ENTRY_TEMPLATE' ?
                                                        deepAssign(state, {
                                                            URL: action.payload.status === RequestStatus.SUCCEEDED ? action.payload.response : '',
                                                            Name: action.payload.filename,
                                                            ValueWriteRuleID: action.payload.valueWriteRule,
                                                            Custom: { status: action.payload.status }
                                                        }) :
                                                        state,
                                                import: (state = e.Custom.template, action: Action<'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA'>) =>
                                                    action.type === 'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA' ?
                                                        deepAssign(state, {
                                                            ...action.payload.source,
                                                            Custom: { status: action.payload.status }
                                                        }) :
                                                        state
                                            })
                                        })
                                    ),
                                    (state, action) => state.getIndexByID(action.payload && (typeof (action.payload as any).response === 'number' ? (action.payload as any).response : (action.payload as any).entityID))
                                ),
                                entityDeleteReducer(es, 'DELETE_FORECAST_MODEL_DATA_ENTRY')
                            )),
                            Custom: combineReducers(l.Custom, {
                                aggregation: switchReducers(as,
                                    entityLoadAllReducer(as, 'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA'),
                                    (state = as, action: Action<'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA'>) =>
                                        action.type === 'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA' &&
                                                action.payload.status === RequestStatus.SUCCEEDED &&
                                                !action.payload.preview ?
                                        // not kewl, causes aggregation to be called twice
                                        // but the problem is that non-preview aggregateData still returns report of what it's going to do, not what it actually does
                                        // TODO make backend return updated data in non-preview mode
                                            as :
                                            state,
                                    entityVoidAllReducer(as, 'ADD_FORECAST_MODEL_DATA_ENTRY'),
                                    entityVoidAllReducer(as, 'EDIT_FORECAST_MODEL_DATA_ENTRY'),
                                    entityVoidAllReducer(as, 'TOUCH_FORECAST_MODEL_DATA_ENTRY'),
                                    entityVoidAllReducer(as, 'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA'),
                                    entityVoidAllReducer(as, 'DELETE_FORECAST_MODEL_DATA_ENTRY')
                                )
                            })
                        }),
                        (state, action) =>
                            action.type === 'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA' ?
                                state.getIndexByID(action.payload && (action.payload as any).entityID) :
                                state.getIndexByID(action.payload && (action.payload as any).parentID)
                    ),
                    entityDeleteAllReducer(ls, 'DELETE_ALL_FORECAST_MODEL_AGGREGATION_LEVELS'),
                    entityDeleteReducer(ls, 'DELETE_FORECAST_MODEL_AGGREGATION_LEVEL')
                ),
                Custom: combineReducers(m.Custom, {
                    status: (state: RequestStatus = e.Custom.status, action: Action<'ADD_FORECAST_MODEL' | 'ADD_ALL_FORECAST_MODEL_AGGREGATION_LEVELS' | 'ADD_FORECAST_MODEL_AGGREGATION_LEVEL'>) =>
                        action.type === 'ADD_FORECAST_MODEL' && action.payload.status === RequestStatus.SUCCEEDED ||
                                action.type === 'ADD_ALL_FORECAST_MODEL_AGGREGATION_LEVELS' && action.payload.autoSubdivide && action.payload.status === RequestStatus.SUCCEEDED ||
                                action.type === 'ADD_FORECAST_MODEL_AGGREGATION_LEVEL' && action.payload.autoSubdivide && action.payload.status === RequestStatus.SUCCEEDED ?
                            RequestStatus.UNSYNCED as RequestStatus :
                            state,
                    aggregation: switchReducers(as,
                        networkActionReducer(as, 'AGGREGATE_FORECAST_MODEL_DATA'),
                        entityVoidAllReducer(as, 'ADD_FORECAST_MODEL_DATA_ENTRY'),
                        entityVoidAllReducer(as, 'EDIT_FORECAST_MODEL_DATA_ENTRY'),
                        entityVoidAllReducer(as, 'TOUCH_FORECAST_MODEL_DATA_ENTRY'),
                        entityVoidAllReducer(as, 'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA'),
                        entityVoidAllReducer(as, 'DELETE_FORECAST_MODEL_DATA_ENTRY')
                    )
                })
            }),
            (state, action) => state.Data.getIndexByID(action.payload && ((action.payload as any).grandParentID || (action.payload as any).parentID || (action.payload as any).response || (action.payload as any).entityID))
        ),
        entityDeleteReducer(ms, 'DELETE_FORECAST_MODEL'),
        (state = ms, action: Action<'EDIT_CASHFLOW_FORECAST_SETTINGS'>) =>
            action.type === 'EDIT_CASHFLOW_FORECAST_SETTINGS' ?
                deepAssign(state, { Custom: { status: action.payload.status !== RequestStatus.SUCCEEDED ? RequestStatus.PENDING : RequestStatus.UNSYNCED } }) :
                state,
        entityVoidAllReducer(ms, 'VOID_ALL_FORECAST_MODELS')
    )
    ),
    forecastModelRecordDatas: switchReducers(frs,
        entityLoadAllReducer(frs, 'LOAD_ALL_FORECAST_MODEL_RECORD_DATA'),
        entityAddReducer(frs, 'ADD_FORECAST_MODEL_RECORD_DATA'),
        entityEditReducer(frs, 'EDIT_FORECAST_MODEL_RECORD_DATA'),
        childrenReducer(frs,
            combineReducers(fr, {
                Custom: combineReducers(fr.Custom, {
                    import: (state, action: Action<'IMPORT_FORECAST_MODEL_RECORD_DATA'>) => {
                        const defaultState = state || fr.Custom;

                        if (action.type === 'IMPORT_FORECAST_MODEL_RECORD_DATA') {
                            return deepAssign(defaultState, {
                                Custom: {
                                    status: action.payload.status === RequestStatus.FAILED ? RequestStatus.FAILED : RequestStatus.PENDING
                                }
                            });
                        }

                        return defaultState;
                    },
                    results: (state, action: Action<'LOAD_FORECAST_MODEL_RECORD_DATA_RESULT_SLICE'>) => {
                        const defaultState = state || fr.Custom.results;
                        let actionOverride = action;

                        if (action.type === 'LOAD_FORECAST_MODEL_RECORD_DATA_RESULT_SLICE' && action.payload.status === RequestStatus.SUCCEEDED) {
                            const data = (state as unknown as entities.NetworkPaginationSlice<entities.AccountingData> || fr.Custom.results).Data
                                .concat(...action.payload.response.Data)
                                .groupBy('ID')
                                .map(group => group.first());

                            actionOverride = deepAssign(action, {
                                payload: {
                                    response: {
                                        Count: data.length,
                                        Data: data
                                    }
                                }
                            });
                        }

                        return entityLoadAllReducer(defaultState, 'LOAD_FORECAST_MODEL_RECORD_DATA_RESULT_SLICE')(defaultState, actionOverride);
                    }
                }),
                Files: switchReducers(frfs,
                    entityLoadReducer(frfs, 'UPLOAD_FORECAST_MODEL_RECORD_DATA_FILE'),
                    entityAddReducer(frfs, 'ADD_FORECAST_MODEL_RECORD_DATA_FILE'),
                    entityEditReducer(frfs, 'EDIT_FORECAST_MODEL_RECORD_DATA_FILE'),
                    childrenReducer(frfs,
                        combineReducers(frf, {
                            ActualSplitRanges: switchReducers(frfas,
                                entityAddReducer(frfas, 'ADD_FORECAST_MODEL_RECORD_DATA_ACTUAL_SPLIT_RANGE'),
                                entityEditReducer(frfas, 'EDIT_FORECAST_MODEL_RECORD_DATA_ACTUAL_SPLIT_RANGE'),
                                entityDeleteReducer(frfas, 'DELETE_FORECAST_MODEL_RECORD_DATA_ACTUAL_SPLIT_RANGE')
                            )
                        }),
                        (state, action) => state.getIndexByID(action.payload?.parentID)
                    ),
                    entityDeleteReducer(frfs, 'DELETE_FORECAST_MODEL_RECORD_DATA_FILE')
                )
            }),
            (state, action) => state.Data.getIndexByID((action.payload as any)?.grandParentID || (action.payload as any)?.parentID || (action.payload as any)?.entityID)
        ),
        entityDeleteReducer(frs, 'DELETE_FORECAST_MODEL_RECORD_DATA'),
        entityVoidAllReducer(frs, 'VOID_ALL_FORECAST_MODEL_RECORD_DATA')
    ),
    forecastModelRecordDataImportPeriodTypes: synchronizableEnumReducer(
        defaults.NetworkArrayOf(defaults.ForecastModelRecordDataImportPeriodType),
        'LOAD_ALL_FORECAST_MODEL_RECORD_DATA_IMPORT_PERIOD_TYPES',
        ForecastModelRecordDataImportPeriodType as any,
        'ForecastModelRecordDataImportPeriodType'
    ),
    vatPeriodTypes: synchronizableEnumReducer(defaults.NetworkArrayOf(defaults.VATPeriodType), 'LOAD_ALL_VAT_PERIOD_TYPES', VATPeriodType as any, 'VATPeriodType'),
    cashflowForecastSettings: switchReducers(f,
        (state = f, action: Action<'LOAD_CASHFLOW_FORECAST_SETTINGS'>) =>
            entityLoadReducer(f, 'LOAD_CASHFLOW_FORECAST_SETTINGS')(state, { ...action, payload: { ...action.payload, entityID: state.ID } } as any), // shitty, TODO add ID-less singleton entities handling to common reducers
        entityEditReducer(f, 'EDIT_CASHFLOW_FORECAST_SETTINGS'),
        networkActionReducer(f, 'RECALCULATE_CASHFLOW_FORECAST'),
        combineReducers(f, {
            IndicatorConnections: switchReducers(cs,
                entityAddReducer(cs, 'ADD_CASHFLOW_FORECAST_INDICATOR_CONNECTION'),
                entityEditReducer(cs, 'EDIT_CASHFLOW_FORECAST_INDICATOR_CONNECTION'),
                childrenReducer(cs,
                    combineReducers(c, {
                        Distribution: sortedEntitiesReducer(ds, 'MonthOffset', switchReducers(ds,
                            entityAddAllReducer(ds, 'ADD_ALL_CASHFLOW_FORECAST_INDICATOR_DISTRIBUTION_ENTRY'),
                            entityEditAllReducer(ds, 'EDIT_ALL_CASHFLOW_FORECAST_INDICATOR_DISTRIBUTION_ENTRY'),
                            entityDeleteAllReducer(ds, 'DELETE_ALL_CASHFLOW_FORECAST_INDICATOR_DISTRIBUTION_ENTRY')
                        ))
                    }),
                    (state, action) => state.getIndexByID(action.payload && ((action.payload as any).parentID || (action.payload as any).entityID))
                ),
                entityDeleteReducer(cs, 'DELETE_CASHFLOW_FORECAST_INDICATOR_CONNECTION')
            )
        })
    ),
    cashflowForecastReport: switchReducers(r,
        (state = r, action: Action<'LOAD_CASHFLOW_FORECAST_REPORT'>) =>
            entityLoadReducer(state, 'LOAD_CASHFLOW_FORECAST_REPORT')(state, { ...action, payload: { ...action.payload, entityID: state.ID } } as any), // shitty, TODO add ID-less singleton entities handling to common reducers
        (state = r, action: Action<'EDIT_CASHFLOW_FORECAST_SETTINGS' | 'RECALCULATE_CASHFLOW_FORECAST'>) =>
            action.type === 'EDIT_CASHFLOW_FORECAST_SETTINGS' || action.type === 'RECALCULATE_CASHFLOW_FORECAST' ?
                deepAssign(state, {
                    ...action.type === 'EDIT_CASHFLOW_FORECAST_SETTINGS' ?
                        action.payload.entity as entities.CashflowForecastSettings as entities.CashflowForecastDataReport :
                        null,
                    Custom: { status: action.payload.status !== RequestStatus.SUCCEEDED ? RequestStatus.PENDING : RequestStatus.UNSYNCED }
                }) :
                state
    )
});
